/**
 * Data mappers for converting GraphQL Coupon types to legacy ICoupon interface
 */

import type { ICoupon } from '../../../../ts/interface/coupon.interface';
import type { CouponGraphQL } from './useCouponsRelay';
import { convertCentsToMoney, timeForHumans } from '../../../../utils';

/**
 * Map GraphQL Coupon to legacy ICoupon interface
 */
export function mapCouponToICoupon(coupon: CouponGraphQL): ICoupon {
  const mapped: ICoupon = {
    id: coupon.oldId ?? coupon.id,
    gid: coupon.id,
    old_id: coupon.oldId ?? '',
    name: coupon.name,
    code: coupon.code ?? undefined,
    active: coupon.active ?? undefined,
    discount_type: coupon.discountType ?? undefined,
    discountActual: coupon.discount ?? undefined,
    uses: coupon.uses ?? undefined,
    duration: coupon.duration ?? undefined,
    created: coupon.created ?? undefined,
    organizationId: coupon.organizationId ?? null,
    systemOldId: coupon.systemOldId ?? undefined,
    // Time-rule + assignment fields pass through as raw values so the edit
    // form can pre-fill (bitmask -> toggles, minutes -> TimePicker, dates ->
    // DatePicker). These are NOT formatted for display.
    assignmentOnly: coupon.assignmentOnly ?? undefined,
    validDays: coupon.validDays ?? null,
    validTimeStart: coupon.validTimeStart ?? null,
    validTimeEnd: coupon.validTimeEnd ?? null,
    validFrom: coupon.validFrom ?? null,
    validUntil: coupon.validUntil ?? null,
  };

  // Format discount for display (matching REST API behavior)
  if (coupon.discount !== null && coupon.discount !== undefined) {
    if (coupon.discountType === 'percent') {
      mapped.discount = `${coupon.discount}%`;
    } else if (coupon.discountType === 'time') {
      mapped.discount = timeForHumans(coupon.discount) as string;
    } else {
      mapped.discount = convertCentsToMoney(coupon.discount);
    }
  }

  // Format duration/uses for display
  if (coupon.duration == null) {
    if (coupon.uses === 1) {
      mapped.uses = `${coupon.uses} Use`;
    } else if (coupon.uses !== null && coupon.uses !== undefined) {
      mapped.uses = `${coupon.uses} Uses`;
    }
  } else {
    mapped.duration = timeForHumans(coupon.duration) as number;
  }

  return mapped;
}

/**
 * Map array of GraphQL coupons to ICoupon array
 */
export function mapCouponsToICoupons(coupons: CouponGraphQL[]): ICoupon[] {
  return coupons.map(mapCouponToICoupon);
}

/**
 * Split coupons into active and retired lists
 */
export function splitCoupons(coupons: CouponGraphQL[]): {
  coupons: ICoupon[];
  retired_coupons: ICoupon[];
} {
  const active: CouponGraphQL[] = [];
  const retired: CouponGraphQL[] = [];

  coupons.forEach((coupon) => {
    if (coupon.active) {
      active.push(coupon);
    } else {
      retired.push(coupon);
    }
  });

  return {
    coupons: mapCouponsToICoupons(active),
    retired_coupons: mapCouponsToICoupons(retired),
  };
}
